import React, { useMemo } from 'react';
import { Button } from '../../../../../../ui';
import {
  PrimaryTypography,
  SecondaryTypography,
} from '../../../../../../ui/typography/typography';
import {
  Product,
  SubscriptionFeatureDetails,
} from '../../../../../../utils/types/admin_types';
// Optionally: import { CheckCircle, Clock, XCircle } from 'lucide-react';

type FeatureCardProps = {
  product: Product;
  companySize: 'Small' | 'Medium' | 'Large';
  interval: 'month' | 'year';
  currentSubscription: SubscriptionFeatureDetails | undefined;
  img: string;
  description: string;
  cardDetailsUnavailable: boolean;
  isTrialUsed: boolean;
  addSubscription: (priceId: string, trial: boolean) => void;
  cancelSubscription: (featureType: string, cancel: boolean) => void;
};

export const FeatureCard = ({
  product,
  companySize,
  interval,
  currentSubscription,
  img,
  description,
  cardDetailsUnavailable,
  isTrialUsed,
  addSubscription,
  cancelSubscription,
}: FeatureCardProps) => {
  const selectedPrice = useMemo(() => {
    if (!product.prices) return null;
    return product.prices.find(
      (price) =>
        price.companySize === companySize && price.interval === interval,
    );
  }, [product, companySize, interval]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className='inline-flex items-center bg-lime-100 text-lime-700 border border-lime-200 px-2 py-0.5 rounded-full text-xs font-medium ml-2 gap-1'>
            { /* <CheckCircle className="w-4 h-4" /> */ }
            Active
          </span>
        );
      case 'trialing':
        return (
          <span className='inline-flex items-center bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded-full text-xs font-medium ml-2 gap-1'>
            { /* <Clock className="w-4 h-4" /> */ }
            Trial
          </span>
        );
      default:
        return (
          <span className='inline-flex items-center bg-rose-50 text-rose-800 border border-rose-200 px-2 py-0.5 rounded-full text-xs font-medium ml-2 gap-1'>
            { /* <XCircle className="w-4 h-4" /> */ }
            Inactive
          </span>
        );
    }
  };

  return (
    <div
        className={`
        flex flex-col bg-white/80 w-full max-w-sm rounded-2xl shadow-lg border border-gray-100
        hover:shadow-2xl transition-all duration-200 min-h-[510px] mx-auto
        backdrop-blur-md
      `}
    >
      { /* Header */ }
      <div className='flex flex-col items-center pt-8 px-6'>
        <div className='flex flex-col items-center gap-2 w-full'>
          <div className='w-20 h-20 rounded-full bg-primary-light flex items-center justify-center shadow-inner border-4 border-white mb-1'>
            <img
                src={img}
                alt={product.feature + ' image'}
                className='w-14 h-14 object-contain'
            />
          </div>
          <h3 className='font-medium text-3xl text-gray-900 tracking-tight mb-1'>
            { product.feature }
          </h3>
          <p className='text-md text-gray-500 text-start leading-snug mb-1'>
            { description }
          </p>
        </div>
      </div>

      { /* Details */ }
      <div className='flex-1 flex flex-col justify-between px-7 py-4'>
        { currentSubscription && (
          <div className='mb-3 flex flex-col gap-1'>
            <p className='flex items-center text-sm text-gray-700 font-medium'>
              Subscription: { getStatusBadge(currentSubscription.status) }
            </p>
            <p className='text-gray-500 text-sm'>
              <span className='font-medium text-gray-700'>Active</span>{ ' ' }
              <span className='font-semibold text-gray-900'>
                { new Date(
                  currentSubscription.currentPeriodStart * 1000,
                ).toLocaleDateString() }
              </span>
              <span className='text-gray-500'> to </span>
              <span className='font-semibold text-gray-900'>
                { new Date(
                  currentSubscription.currentPeriodEnd * 1000,
                ).toLocaleDateString() }
              </span>
            </p>
            { currentSubscription.cancelAt && (
              <p className='text-xs text-rose-700 font-medium'>
                Will cancel on{ ' ' }
                <span className='font-semibold text-gray-900'>
                  { new Date(
                    currentSubscription.cancelAt * 1000,
                  ).toLocaleDateString() }
                </span>
              </p>
            ) }
          </div>
        ) }

        <div className='mb-2 flex flex-col'>
          <span className='text-gray-500 text-xs font-medium mb-1'>
            Current Plan
          </span>
          <span className='text-primary-main font-bold text-2xl'>
            { selectedPrice ? `USD ${selectedPrice.unitAmount}` : '--' }
            <span className='text-xs text-gray-400 font-normal ml-1'>
              { interval === 'month' ? '/month' : '/year' }
            </span>
          </span>
        </div>
      </div>

      { /* CTA Footer */ }
      <div className='flex items-center justify-center bg-slate-50 px-6 py-4 rounded-b-2xl border-t border-slate-100 mt-auto'>
        { !currentSubscription ? (
          <div className='flex gap-3 w-full'>
            { !isTrialUsed && (
              <Button
                  variant='secondary'
                  onClick={() =>
                    selectedPrice && addSubscription(selectedPrice.priceId, true)
                  }
                  label='Start Free Trial'
                  width='100%'
              />
            ) }
            <Button
                variant='primary'
                onClick={() =>
                  selectedPrice && addSubscription(selectedPrice.priceId, false)
                }
                label='Upgrade Now'
                disabled={cardDetailsUnavailable}
                width='100%'
            />
          </div>
        ) : currentSubscription.cancelAt ? (
          <Button
              variant='primary'
              onClick={() => cancelSubscription(product.feature, false)}
              label='Renew Subscription'
              disabled={cardDetailsUnavailable}
              width='100%'
          />
        ) : (
          <Button
              variant='secondary'
              onClick={() => cancelSubscription(product.feature, true)}
              label='Cancel Subscription'
              width='100%'
          />
        ) }
      </div>
    </div>
  );
};
